import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { useTranslation } from "react-i18next";
import apiClient from "../utils/apiClient";
import { brandColor } from "../theme";
import dayjs from "dayjs";

import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/ar";
import "dayjs/locale/tr";
import "dayjs/locale/hy-am";

type AgentDetailingClientsProps = {
  agentTnId: string;
  agentName: string;
  month: string;
  year: string;
};

const dayjsLocaleMap: Record<string, string> = {
  ru: "ru",
  en: "en",
  ar: "ar",
  tr: "tr",
  hy: "hy-am",
};

const AgentDetailingClients = ({
  agentTnId,
  agentName,
  month,
  year,
}: AgentDetailingClientsProps) => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [currency, setCurrency] = useState<string>();

  const currentLanguage = i18n.language?.split("-")[0] || "ru";
  const dayjsLocale = dayjsLocaleMap[currentLanguage] || "ru";

  const periodLabel = useMemo(
    () => dayjs(`${year}-${month}-01`).locale(dayjsLocale).format("MMMM YYYY"),
    [year, month, dayjsLocale],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "fullname",
        headerName: t("agentDetailingClients.columns.fio"),
        flex: 2,
      },
      {
        field: "brokerage",
        headerName: t("agentDetailingClients.columns.brokerage"),
        flex: 1,
        type: "number",
      },
      {
        field: "retention",
        headerName: t("agentDetailingClients.columns.retention"),
        flex: 1,
        type: "number",
      },
      {
        field: "acquisition",
        headerName: t("agentDetailingClients.columns.acquisition"),
        flex: 1,
        type: "number",
      },
      {
        field: "total",
        headerName: t("agentDetailingClients.columns.total"),
        flex: 1,
        type: "number",
      },
    ],
    [t],
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get("/detailing/clients", {
          params: { agent_tn_id: agentTnId, month, year },
        });
        if (response.data?.success) {
          setRows(response.data.rows ?? []);
          setCurrency(response.data.currency);
        }
      } catch (error) {
        console.error("Ошибка загрузки детализации клиентов", error);
      } finally {
        setLoading(false);
      }
    };

    if (agentTnId) {
      fetchData();
    }
  }, [agentTnId, month, year]);

  const total = useMemo(() => {
    if (!rows.length) return {};

    const r = rows.reduce(
      (acc, row) => ({
        brokerage: acc.brokerage + row.brokerage,
        retention: acc.retention + row.retention,
        acquisition: acc.acquisition + row.acquisition,
        total: acc.total + row.total,
      }),
      { brokerage: 0, retention: 0, acquisition: 0, total: 0 },
    );

    return {
      brokerage: r.brokerage.toFixed(2),
      retention: r.retention.toFixed(2),
      acquisition: r.acquisition.toFixed(2),
      total: r.total.toFixed(2),
    };
  }, [rows]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        px: 2,
        pb: 2,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("agentDetailingClients.period")}: <b>{periodLabel}</b>
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{
          p: 1,
          borderLeft: "3px solid " + brandColor,
          backgroundColor: "#FFFDE7",
          mb: 1,
        }}
      >
        <Typography variant="body2">
          <b>{t("agentDetailingClients.total")}</b>
          {currency ? `, ${currency}` : ""}
        </Typography>

        <Typography variant="body2">
          {t("agentDetailingClients.columns.brokerage")}:{" "}
          <strong>{total?.brokerage ?? 0}</strong>
        </Typography>

        <Typography variant="body2">
          {t("agentDetailingClients.columns.retention")}:{" "}
          <strong>{total?.retention ?? 0}</strong>
        </Typography>

        <Typography variant="body2">
          {t("agentDetailingClients.columns.acquisition")}:{" "}
          <strong>{total?.acquisition ?? 0}</strong>
        </Typography>

        <Typography variant="body2">
          {t("agentDetailingClients.columns.total")}:{" "}
          <strong>{total?.total ?? 0}</strong>
        </Typography>
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        sx={{
          "--DataGrid-rowBorderColor": "#D1D3D4",
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            fontSize: 12,
            lineHeight: "110%",
          },
          "& .MuiDataGrid-cell": {
            fontSize: 16,
            borderTop: "0.5px solid #D1D3D4",
          },
          fontFamily: "inherit",
          borderWidth: 0,
          background: "#ffffff",
          flex: 1,
        }}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
    </Box>
  );
};

export default AgentDetailingClients;
